import React, {useEffect, useRef, useState} from 'react';
import { setSettingsDirty } from "../../../../../util/settingsDirty";
import { dirtyFingerprint } from "../../../../../util/useDirtyTracker";
import { useTranslation } from 'react-i18next';
import './BusinessCalendarComponent.css';
import {BusinessSettings} from "../../../../../model/business/BusinessSettings";
import {BusinessHours} from "../../../../../model/business/BusinessHours";
import {generateTimeOptions} from "../../../../util/Utils";
import { useTimeFormat, is24h, labelFor, formatHHmm } from "../../../../../util/timeFormat";
import Select from 'react-select';
import { useAuth0 } from '../../../../../auth/useAuth0';
import {useBusinessContext} from "../../../../../context/BusinessContext";
import {useAlert} from "../../../../../context/alert/AlertContext";
import {
    findBusinessHoursByBusinessId,
    findBusinessSettingByBusinessId, saveBusinessHours, deleteBusinessHours,
    saveBusinessSettings
} from "../../../../../service/business/business-service";
import {convertTo12HourFormat, mapListSourceToTarget, mapSourceToTarget} from "../../../../../util/util";
import {BusinessSettingsInput} from "../../../../../service/business/BusinessSettingsInput";
import ActionsButtonsComponent from "../../../../commons/action-buttons/ActionsButtonsComponent";
import {BusinessHoursInput} from "../../../../../service/business/BusinessHoursInput";
import { SettingsField, ToggleRow, NumberWithSuffix } from "../../../../commons/settings-row/SettingsRow";
import { ONLINE_PAYMENTS_ENABLED } from "../../../../../util/onlinePayments";


// Canonical day-of-week values across the whole project — UPPER full form.
type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

/** Accepts any legacy alias ("Mon", "Monday", "MONDAY") and returns canonical UPPER full. */
const normalizeDow = (s: string | null | undefined): DayOfWeek | null => {
    if (!s) return null;
    const prefix = s.trim().toUpperCase().slice(0, 3);
    const map: Record<string, DayOfWeek> = {
        MON: 'MONDAY', TUE: 'TUESDAY', WED: 'WEDNESDAY',
        THU: 'THURSDAY', FRI: 'FRIDAY', SAT: 'SATURDAY', SUN: 'SUNDAY',
    };
    return map[prefix] ?? null;
};

// NB: times MUST match generateTimeOptions' format exactly — "8:00 AM", not
// "08:00 AM" (no leading zero). react-select matches the selected value against
// the option list by string, so a zero-padded default falls out of the grid and
// the combo opens blank / on the wrong row (CA-01).
const initialBusinessHours: Record<DayOfWeek, BusinessHours> = {
    MONDAY:    { id: null, dayOfWeek: "MONDAY",    openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
    TUESDAY:   { id: null, dayOfWeek: "TUESDAY",   openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
    WEDNESDAY: { id: null, dayOfWeek: "WEDNESDAY", openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
    THURSDAY:  { id: null, dayOfWeek: "THURSDAY",  openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
    FRIDAY:    { id: null, dayOfWeek: "FRIDAY",    openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
    SATURDAY:  { id: null, dayOfWeek: "SATURDAY",  openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
    SUNDAY:    { id: null, dayOfWeek: "SUNDAY",    openTime: "8:00 AM", closeTime: "5:00 PM", businessId: null },
};

const mapBackendHoursToState = (backendHours: BusinessHours[]): Record<DayOfWeek, BusinessHours> => {
    const defaultBusinessHours = { ...initialBusinessHours };
    backendHours.forEach(({ dayOfWeek, openTime, closeTime, breakStart, breakEnd, overnightEnd, id, businessId }) => {
        const day = normalizeDow(dayOfWeek);
        if (day) {
            defaultBusinessHours[day] = {
                id,
                dayOfWeek: day,
                openTime: convertTo12HourFormat(openTime || ''),
                closeTime: convertTo12HourFormat(closeTime || ''),
                breakStart: breakStart ? convertTo12HourFormat(breakStart) : null,
                breakEnd: breakEnd ? convertTo12HourFormat(breakEnd) : null,
                overnightEnd: overnightEnd ? convertTo12HourFormat(overnightEnd) : null,
                businessId,
            };
        }
    });
    return defaultBusinessHours;
};

/**
 * react-select styles for the open/close time pickers. Uses --gs-* design
 * tokens so the dropdowns follow light/dark theme (the default react-select
 * theme is hardcoded white). menu also caps its height.
 */
const TIME_SELECT_STYLES = {
    control: (base: any, state: any) => ({
        ...base,
        backgroundColor: 'var(--gs-surface, #fff)',
        borderColor: state.isFocused
            ? 'var(--gs-primary, #7266e8)'
            : 'var(--gs-border-strong, #d7d7e2)',
    }),
    menu: (base: any) => ({
        ...base,
        maxHeight: '300px',
        backgroundColor: 'var(--gs-surface, #fff)',
        border: '1px solid var(--gs-border, #ececf4)',
    }),
    singleValue: (base: any) => ({ ...base, color: 'var(--gs-text-body, #1d1a33)' }),
    input: (base: any) => ({ ...base, color: 'var(--gs-text-body, #1d1a33)' }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--gs-primary, #7266e8)'
            : state.isFocused
                ? 'var(--gs-primary-softer, #efedfd)'
                : 'var(--gs-surface, #fff)',
        color: state.isSelected ? '#fff' : 'var(--gs-text-body, #1d1a33)',
    }),
};

/** Parse a "h:mm AM/PM" time (as produced by generateTimeOptions) to minutes
 *  since midnight, or null when it can't be parsed. */
const timeToMinutes = (value?: string): number | null => {
    const m = (value || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let hour = parseInt(m[1], 10) % 12;
    if (m[3].toUpperCase() === 'PM') hour += 12;
    return hour * 60 + parseInt(m[2], 10);
};

/** Close-time minutes, treating midnight (00:00 / "12:00 AM") as END OF DAY (24:00)
 *  so a day can legitimately close at midnight (required for the overnight setup). */
const closeToMinutes = (value?: string): number | null => {
    const m = timeToMinutes(value);
    if (m === null) return null;
    return m === 0 ? 24 * 60 : m;
};

/** Day-of-week order + the next day's key (overnight belongs to the NEXT day). */
const DOW_ORDER: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const nextDow = (day: DayOfWeek): DayOfWeek => DOW_ORDER[(DOW_ORDER.indexOf(day) + 1) % 7];

/** A selected day's hours are invalid when its close time is not strictly after
 *  its open time (ticket #18). Midnight close counts as end-of-day, so it's valid. */
const isHoursRangeInvalid = (openTime?: string, closeTime?: string): boolean => {
    const open = timeToMinutes(openTime);
    const close = closeToMinutes(closeTime);
    if (open === null || close === null) return false; // can't judge → don't block
    return close <= open;
};

/** Break (when enabled) must sit fully inside the day window: open ≤ breakStart < breakEnd ≤ close. */
const isBreakInvalid = (
    openTime?: string, closeTime?: string,
    breakStart?: string | null, breakEnd?: string | null,
): boolean => {
    if (!breakStart && !breakEnd) return false; // no break configured
    const o = timeToMinutes(openTime);
    const c = closeToMinutes(closeTime);
    const s = timeToMinutes(breakStart || undefined);
    const e = timeToMinutes(breakEnd || undefined);
    if (s === null || e === null) return true;     // partially set
    if (s >= e) return true;                        // start must be before end
    if (o !== null && s < o) return true;           // inside the day window
    if (c !== null && e > c) return true;
    return false;
};

/** Overnight block [00:00, overnightEnd) lands on the NEXT day's early morning.
 *  Valid when: it's after midnight, not past noon, AND the day closes EXACTLY at
 *  midnight — overnight must continue seamlessly from the day (no gap before 00:00). */
const isOvernightInvalid = (closeTime?: string, overnightEnd?: string | null): boolean => {
    if (!overnightEnd) return false; // no overnight configured
    const v = timeToMinutes(overnightEnd);
    if (v === null || v <= 0) return true;          // must be after midnight
    if (v > 12 * 60) return true;                   // sane cap: early morning only (≤ noon)
    if (closeToMinutes(closeTime) !== 24 * 60) return true; // day must close at midnight
    return false;
};

const BusinessCalendarComponent: React.FC = () => {
    const { t } = useTranslation();
    const timeFormat = useTimeFormat();
    const [businessHours, setBusinessHours] = useState(initialBusinessHours);
    const timeOptions = generateTimeOptions(is24h(timeFormat));
    /** react-select foca/rola até a opção selecionada comparando por REFERÊNCIA —
     *  um objeto {value,label} construído inline nunca é o mesmo da lista, então o
     *  menu abria no topo (00:00) em vez do horário atual. Devolve a opção da
     *  própria lista; valores fora da grade caem num fallback só para exibição. */
    const timeOption = (v?: string | null) =>
        timeOptions.find((o) => o.value === v) ?? { value: v || '', label: labelFor(v || '', timeFormat) };
    const { getAccessTokenSilently, logout } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();

    // Estados locais
    const [settingsData, setSettingsData] = useState<BusinessSettings | null>(null);

    // QA §5.10: dirty DERIVADO de baseline. Esta aba tem DOIS estados (regras +
    // horários), então guardamos um baseline de cada; dirty = qualquer um diferiu.
    // Sem isso, digitar um char rejeitado ou editar-e-voltar deixava o "Salvar".
    const settingsBaseline = useRef<string>('');
    const hoursBaseline = useRef<string>('');
    const [isModified, setIsModified] = useState(false);
    const recomputeDirty = () => {
        const ready = settingsBaseline.current !== '' && hoursBaseline.current !== '';
        const dirty = ready && (
            dirtyFingerprint(settingsData) !== settingsBaseline.current ||
            dirtyFingerprint(businessHours) !== hoursBaseline.current
        );
        setIsModified(dirty);
        setSettingsDirty(dirty);
    };
    useEffect(() => {
        recomputeDirty();
        return () => setSettingsDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsData, businessHours]);


    // Fetch Business Settings
    useEffect(() => {
        if (settingsData) {
            // Se os dados já estão carregados, não faz nada
            return;
        }
        const fetchBusinessSetting = async () => {
            try {
                const token = await getAccessTokenSilently();
                if (!selectedBusiness || !selectedBusiness.id) {
                    setAlert('danger', t('settings.business.alerts.settingsNotLoaded'));
                    return;
                }
                const fetchedBusinessSetting = await findBusinessSettingByBusinessId(selectedBusiness.id, token);
                if (fetchedBusinessSetting) {
                    if (selectedBusiness.id !== fetchedBusinessSetting.businessId) {
                        await logout();
                    }
                    settingsBaseline.current = dirtyFingerprint(fetchedBusinessSetting);
                    setSettingsData(fetchedBusinessSetting);
                }
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                setAlert('danger', t('settings.business.alerts.settingsFetchError', { error: errorMessage }));
            }
        };

        const fetchBusinessHours = async () => {
            try {
                const token = await getAccessTokenSilently();
                if (!selectedBusiness || !selectedBusiness.id) {
                    setAlert('danger', t('settings.business.alerts.hoursNotLoaded'));
                    return;
                }
                const fetchedBusinessHours = await findBusinessHoursByBusinessId(selectedBusiness.id, token);
                if (fetchedBusinessHours) {
                    const mapped = mapBackendHoursToState(fetchedBusinessHours);
                    hoursBaseline.current = dirtyFingerprint(mapped);
                    setBusinessHours(mapped);
                }
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                setAlert('danger', t('settings.business.alerts.hoursFetchError', { error: errorMessage }));
            }
        };

        fetchBusinessSetting();
        fetchBusinessHours();
    }, [getAccessTokenSilently, selectedBusiness, logout, setAlert, settingsData]);

    const handleCheckboxChange = (day: DayOfWeek) => {
        setBusinessHours(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                businessId: !prev[day].businessId
            }
        }));
    };

    const handleTimeChange = (
        day: DayOfWeek,
        timeType: 'openTime' | 'closeTime' | 'breakStart' | 'breakEnd' | 'overnightEnd',
        value: string,
    ) => {
        setBusinessHours(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [timeType]: value
            }
        }));
    };

    /** Toggle the mid-day break for a day: seed sensible defaults on, clear (null) off. */
    const toggleBreak = (day: DayOfWeek) => {
        setBusinessHours(prev => {
            const cur = prev[day];
            const on = !!cur.breakStart || !!cur.breakEnd;
            return {
                ...prev,
                [day]: {
                    ...cur,
                    breakStart: on ? null : '12:00 PM',
                    breakEnd: on ? null : '1:00 PM',
                },
            };
        });
    };

    /** Toggle the early-morning (overnight) block: seed 2:00 AM on, clear (null) off.
     *  Overnight requires the day to close at midnight, so enabling it also snaps the
     *  close time to 12:00 AM (the madrugada continues seamlessly from there). */
    const toggleOvernight = (day: DayOfWeek) => {
        setBusinessHours(prev => {
            const cur = prev[day];
            const on = !!cur.overnightEnd;
            return {
                ...prev,
                [day]: on
                    ? { ...cur, overnightEnd: null }
                    : { ...cur, overnightEnd: '2:00 AM', closeTime: '12:00 AM' },
            };
        });
    };

    // Save Business Settings
    const handleSave = async () => {
        // Block save when any selected day has an invalid main window, break, or overnight.
        const invalidDay = Object.entries(businessHours).find(
            ([, h]) => h.businessId && (
                isHoursRangeInvalid(h.openTime, h.closeTime) ||
                isBreakInvalid(h.openTime, h.closeTime, h.breakStart, h.breakEnd) ||
                isOvernightInvalid(h.closeTime, h.overnightEnd)
            ),
        );
        if (invalidDay) {
            setAlert('danger', t('settings.calendar.hoursInvalidRangeAlert'));
            return;
        }

        const token = await getAccessTokenSilently();

        try {
            // Save Business Notification Settings if they have been modified
            if (settingsData) {
                const settingsInput: BusinessSettingsInput = mapSourceToTarget(settingsData, BusinessSettingsInput);
                const settingsResult = await saveBusinessSettings(settingsInput, token);

                if (!settingsResult.success) {
                    setAlert('danger', t('settings.business.alerts.notificationSaveError', { error: settingsResult.error }));
                    return;
                } else {
                    setAlert('success', t('settings.business.alerts.calendarSettingsSaved'));
                }
            }

            // Save Business Hours if they have been modified
            if (businessHours) {
                const businessHoursInput: BusinessHoursInput[] = Object.entries(businessHours)
                    .filter(([, { businessId }]) => businessId) // Inclui apenas dias selecionados
                    .map(([day, { id, openTime, closeTime, breakStart, breakEnd, overnightEnd }]) => ({
                        dayOfWeek: day,
                        openTime: openTime || null,
                        closeTime: closeTime || null,
                        breakStart: breakStart || null,
                        breakEnd: breakEnd || null,
                        overnightEnd: overnightEnd || null,
                        businessId: selectedBusiness?.id || null,
                        id: id || null,
                    }));

                // Days turned OFF that were previously saved (have an id) must be
                // removed — the save payload only carries ON days, so without this the
                // old row lingers and the day comes back after reload.
                const toDelete = (Object.entries(businessHours) as [DayOfWeek, BusinessHours][])
                    .filter(([, h]) => !h.businessId && h.id)
                    .map(([day, h]) => ({ day, id: String(h.id) }));

                let hoursChanged = false;
                if (businessHoursInput.length > 0) {
                    const hoursResult = await saveBusinessHours(businessHoursInput, token);
                    if (!hoursResult.success) {
                        setAlert('danger', t('settings.business.alerts.hoursSaveError', { error: hoursResult.error }));
                        return;
                    }
                    hoursChanged = true;
                }

                for (const { id } of toDelete) {
                    await deleteBusinessHours(id, token);
                }
                if (toDelete.length > 0) {
                    // Drop the ids from state so a re-save doesn't touch them again.
                    setBusinessHours((prev) => {
                        const next = { ...prev };
                        toDelete.forEach(({ day }) => { next[day] = { ...next[day], id: null }; });
                        return next;
                    });
                    hoursChanged = true;
                }
                if (hoursChanged) {
                    setAlert('success', t('settings.business.alerts.hoursSaved'));
                }
            }

            // O que foi salvo vira o novo baseline limpo (ambos os estados).
            settingsBaseline.current = dirtyFingerprint(settingsData);
            hoursBaseline.current = dirtyFingerprint(businessHours);
            recomputeDirty();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            setAlert('danger', t('settings.business.alerts.dataSaveError', { error: errorMessage }));
        }
    };


    // Cancel Modifications
    const handleCancel = () => {
        // Recarrega as configurações para o estado original
        const reloadBusinessSettings = async () => {
            const token = await getAccessTokenSilently();
            const fetchedBusinessSetting = await findBusinessSettingByBusinessId(selectedBusiness!.id, token);
            settingsBaseline.current = dirtyFingerprint(fetchedBusinessSetting);
            setSettingsData(fetchedBusinessSetting);
        };

        // Recarrega as configurações para o estado original
        const reloadBusinessHours = async () => {
            const token = await getAccessTokenSilently();
            const fetchedBusinessHours = await findBusinessHoursByBusinessId(selectedBusiness!.id, token);
            const mapped = mapBackendHoursToState(fetchedBusinessHours);
            hoursBaseline.current = dirtyFingerprint(mapped);
            setBusinessHours(mapped);
        };

        reloadBusinessSettings();
        reloadBusinessHours();
    };

    type BusinessSettingsField = keyof BusinessSettingsInput | `${keyof BusinessSettingsInput & string}.${string}`;

    const handleInputChange = (field: BusinessSettingsField, value: any) => {
        setSettingsData((prevData) => {
            if (!prevData) {
                // Retorna o estado original se `prevData` for `null`
                return prevData;
            }

            if (field.includes('.')) {
                const [parentKey, childKey] = field.split('.') as [keyof BusinessSettingsInput, string];

                // Verifique se `prevData[parentKey]` é um objeto válido
                const parentValue = typeof prevData[parentKey] === 'object' && prevData[parentKey] !== null
                    ? prevData[parentKey]
                    : {};

                return {
                    ...prevData,
                    [parentKey]: {
                        ...parentValue,
                        [childKey]: value,
                    },
                };
            } else {
                return {
                    ...prevData,
                    [field]: value,
                };
            }
        });
    };


    return (
        <>
            <ActionsButtonsComponent
                isModified={isModified}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="side-content-horizontal calendar-hours-layout">
                {/* Main settings Section */}
                <div className="business-section-container separated-container">
                    <h3 className="section-header">{t('settings.calendar.sectionTitle')}</h3>
                    <form className="address-form">
                        <div className="gs-settings-grid">
                            <SettingsField label={t('settings.calendar.onlineBooking')} htmlFor="onlineBookingType">
                                <select
                                    id="onlineBookingType"
                                    data-cy="biz-cal-online-booking-select"
                                    value={settingsData?.onlineBookingType || 'ALL'}
                                    onChange={(e) => handleInputChange('onlineBookingType', e.target.value)}
                                >
                                    <option value="ALL">{t('settings.calendar.onlineBookingAll')}</option>
                                    <option value="ONLY_CLIENT">{t('settings.calendar.onlineBookingOnlyClient')}</option>
                                    <option value="ONLY_FAVORITE">{t('settings.calendar.onlineBookingOnlyFavorite')}</option>
                                    <option value="NONE">{t('settings.calendar.onlineBookingNone')}</option>
                                </select>
                            </SettingsField>

                            <SettingsField label={t('settings.calendar.autoApprove')} htmlFor="autoApproveAppointment">
                                <select
                                    id="autoApproveAppointment"
                                    data-cy="biz-cal-auto-approve-select"
                                    value={settingsData?.autoApproveAppointment || 'NEVER'}
                                    onChange={(e) => handleInputChange('autoApproveAppointment', e.target.value)}
                                >
                                    <option value="ALWAYS_APPROVED">{t('settings.calendar.autoApproveAlways')}</option>
                                    <option value="ONLY_CLIENT">{t('settings.calendar.autoApproveOnlyClient')}</option>
                                    <option value="ONLY_FAVORITE">{t('settings.calendar.autoApproveOnlyFavorite')}</option>
                                    <option value="NEVER">{t('settings.calendar.autoApproveNever')}</option>
                                </select>
                            </SettingsField>

                            <SettingsField label={t('settings.calendar.bookingWindow')} htmlFor="bookingWindow">
                                <NumberWithSuffix
                                    id="bookingWindow"
                                    integer
                                    max={43200}
                                    value={settingsData?.bookingWindow ?? ''}
                                    onChange={(v) => handleInputChange('bookingWindow', v)}
                                    suffix={t('settings.calendar.bookingWindowSuffix', { count: Number(settingsData?.bookingWindow) || 0 })}
                                    placeholder={t('settings.calendar.bookingWindowPlaceholder')}
                                />
                                <small className="form-text text-muted">
                                    {t('settings.calendar.bookingWindowHint')}
                                </small>
                            </SettingsField>

                            <SettingsField label={t('settings.calendar.bookingIncrement')} htmlFor="bookingIncrement">
                                <NumberWithSuffix
                                    id="bookingIncrement"
                                    integer
                                    max={480}
                                    value={settingsData?.bookingIncrement ?? ''}
                                    onChange={(v) => handleInputChange('bookingIncrement', v)}
                                    suffix={t('settings.calendar.bookingIncrementSuffix', { count: Number(settingsData?.bookingIncrement) || 0 })}
                                    placeholder={t('settings.calendar.bookingIncrementPlaceholder')}
                                />
                                <small className="form-text text-muted">
                                    {t('settings.calendar.bookingIncrementHint')}
                                </small>
                            </SettingsField>

                            <SettingsField label={t('settings.calendar.cardOnBooking')} htmlFor="cardOnBookingType">
                                <select
                                    id="cardOnBookingType"
                                    data-cy="biz-cal-card-on-booking-select"
                                    value={settingsData?.cardOnBookingType || 'NONE'}
                                    onChange={(e) => handleInputChange('cardOnBookingType', e.target.value)}
                                    // Platform flag: card capture needs online payments (Stripe).
                                    // discovery-service ignores the rule while the flag is off.
                                    disabled={!ONLINE_PAYMENTS_ENABLED}
                                >
                                    <option value="NONE">{t('settings.calendar.cardOnBookingNone')}</option>
                                    <option value="ALL">{t('settings.calendar.cardOnBookingAll')}</option>
                                    <option value="ONLY_NON_CLIENTS">{t('settings.calendar.cardOnBookingOnlyNonClients')}</option>
                                    <option value="ONLY_NEW_CLIENTS">{t('settings.calendar.cardOnBookingOnlyNewClients')}</option>
                                    <option value="ONLY_CLIENT_AND_FAVORITE">{t('settings.calendar.cardOnBookingAllClients')}</option>
                                    <option value="CLIENTS_EXCEPT_FAVORITE">{t('settings.calendar.cardOnBookingExceptFavorite')}</option>
                                </select>
                                <small className="form-text text-muted">
                                    {ONLINE_PAYMENTS_ENABLED
                                        ? t('settings.calendar.cardOnBookingHint')
                                        : t('settings.calendar.cardOnBookingDisabledHint')}
                                </small>
                            </SettingsField>

                            {(settingsData?.cardOnBookingType && settingsData.cardOnBookingType !== 'NONE') && (
                                <SettingsField label={t('settings.calendar.preAuthAmount')} htmlFor="preAuthorizationPercentage">
                                    <NumberWithSuffix
                                        id="preAuthorizationPercentage"
                                        max={100}
                                        value={settingsData?.preAuthorizationPercentage ?? ''}
                                        onChange={(v) => handleInputChange('preAuthorizationPercentage', v)}
                                        suffix={t('settings.calendar.preAuthSuffix')}
                                        placeholder={t('settings.calendar.preAuthPlaceholder')}
                                    />
                                    <small className="form-text text-muted">
                                        {t('settings.calendar.preAuthHint')}
                                    </small>
                                </SettingsField>
                            )}
                        </div>
                    </form>
                </div>
                <div className="calendar-settings-container separated-container">
                    <h3 className="section-header">{t('settings.calendar.hoursTitle')}</h3>
                    <div className="business-title-header">
                        <label className="days-tittle">{t('settings.calendar.hoursDays')}</label>
                        <label className="open-tittle">{t('settings.calendar.hoursOpenAt')}</label>
                        <label className="close-tittle">{t('settings.calendar.hoursCloseAt')}</label>
                    </div>

                    <div className="business-hours-grid">
                        {Object.keys(businessHours).map((day) => (
                            <div key={day} className="business-hours-day">
                            <div className="business-hours-row">
                                <label className="day-checkbox">
                                    <input
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        data-cy={`biz-hours-${day.toLowerCase()}-toggle`}
                                        checked={!!businessHours[day as DayOfWeek].businessId}
                                        onChange={() => handleCheckboxChange(day as DayOfWeek)}/>
                                    <span className="day-label">
                                        {t(`settings.calendar.days.${day}`, { defaultValue: day })}
                                    </span>
                                </label>

                                <div className="time-select">
                                    <Select
                                        value={timeOption(businessHours[day as DayOfWeek].openTime)}
                                        onChange={(selectedOption) => handleTimeChange(day as DayOfWeek, 'openTime', selectedOption?.value || '')}
                                        options={timeOptions}
                                        isSearchable
                                        inputId={`biz-hours-${day.toLowerCase()}-open`}
                                        styles={TIME_SELECT_STYLES}/>
                                </div>

                                <div className="time-select">
                                    <Select
                                        value={timeOption(businessHours[day as DayOfWeek].closeTime)}
                                        onChange={(selectedOption) => handleTimeChange(day as DayOfWeek, 'closeTime', selectedOption?.value || '')}
                                        options={timeOptions}
                                        isSearchable
                                        inputId={`biz-hours-${day.toLowerCase()}-close`}
                                        styles={TIME_SELECT_STYLES}/>
                                </div>
                            </div>

                            {/* Break + Overnight controls — only for selected days */}
                            {businessHours[day as DayOfWeek].businessId && (
                                <div className="hours-extra">
                                    <div className="hours-extra__group">
                                        <label className="gs-inline-check">
                                            <input
                                                type="checkbox"
                                                className="gs-inline-check__box"
                                                data-cy={`biz-hours-${day.toLowerCase()}-break-toggle`}
                                                checked={!!businessHours[day as DayOfWeek].breakStart}
                                                onChange={() => toggleBreak(day as DayOfWeek)}/>
                                            <span className="hours-extra__label">{t('settings.calendar.breakLabel')}</span>
                                        </label>
                                        {businessHours[day as DayOfWeek].breakStart && (
                                            <div className="hours-extra__times">
                                                <div className="time-select time-select--sm">
                                                    <Select
                                                        value={timeOption(businessHours[day as DayOfWeek].breakStart)}
                                                        onChange={(o) => handleTimeChange(day as DayOfWeek, 'breakStart', o?.value || '')}
                                                        options={timeOptions} isSearchable styles={TIME_SELECT_STYLES}/>
                                                </div>
                                                <span className="hours-extra__sep">→</span>
                                                <div className="time-select time-select--sm">
                                                    <Select
                                                        value={timeOption(businessHours[day as DayOfWeek].breakEnd)}
                                                        onChange={(o) => handleTimeChange(day as DayOfWeek, 'breakEnd', o?.value || '')}
                                                        options={timeOptions} isSearchable styles={TIME_SELECT_STYLES}/>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="hours-extra__group">
                                        <label className="gs-inline-check">
                                            <input
                                                type="checkbox"
                                                className="gs-inline-check__box"
                                                data-cy={`biz-hours-${day.toLowerCase()}-overnight-toggle`}
                                                checked={!!businessHours[day as DayOfWeek].overnightEnd}
                                                onChange={() => toggleOvernight(day as DayOfWeek)}/>
                                            <span className="hours-extra__label">{t('settings.calendar.overnightLabel')}</span>
                                        </label>
                                        {businessHours[day as DayOfWeek].overnightEnd && (
                                            <div className="hours-extra__times hours-extra__times--col">
                                                <div className="hours-extra__times">
                                                    <span className="hours-extra__sep">
                                                        {t(`settings.calendar.days.${nextDow(day as DayOfWeek)}`, { defaultValue: nextDow(day as DayOfWeek) })} {formatHHmm('00:00', timeFormat)} →
                                                    </span>
                                                    <div className="time-select time-select--sm">
                                                        <Select
                                                            value={timeOption(businessHours[day as DayOfWeek].overnightEnd)}
                                                            onChange={(o) => handleTimeChange(day as DayOfWeek, 'overnightEnd', o?.value || '')}
                                                            options={timeOptions} isSearchable styles={TIME_SELECT_STYLES}/>
                                                    </div>
                                                </div>
                                                <span className="hours-extra__hint">
                                                    {t('settings.calendar.overnightHint', {
                                                        day: t(`settings.calendar.days.${nextDow(day as DayOfWeek)}`, { defaultValue: nextDow(day as DayOfWeek) }),
                                                        time: formatHHmm('00:00', timeFormat),
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Validation errors */}
                            {businessHours[day as DayOfWeek].businessId && isHoursRangeInvalid(businessHours[day as DayOfWeek].openTime, businessHours[day as DayOfWeek].closeTime) && (
                                <span className="hours-row-error" data-cy={`biz-hours-${day.toLowerCase()}-error`}>{t('settings.calendar.hoursInvalidRange')}</span>
                            )}
                            {businessHours[day as DayOfWeek].businessId && isBreakInvalid(businessHours[day as DayOfWeek].openTime, businessHours[day as DayOfWeek].closeTime, businessHours[day as DayOfWeek].breakStart, businessHours[day as DayOfWeek].breakEnd) && (
                                <span className="hours-row-error" data-cy={`biz-hours-${day.toLowerCase()}-break-error`}>{t('settings.calendar.hoursBreakInvalid')}</span>
                            )}
                            {businessHours[day as DayOfWeek].businessId && isOvernightInvalid(businessHours[day as DayOfWeek].closeTime, businessHours[day as DayOfWeek].overnightEnd) && (
                                <span className="hours-row-error" data-cy={`biz-hours-${day.toLowerCase()}-overnight-error`}>{t('settings.calendar.hoursOvernightInvalid')}</span>
                            )}
                            </div>
                        ))}
                    </div>

                </div>
            </div>
            <div className="side-content-vertical">
                {/* Main settings Section */}
                <div className="business-section-container separated-container">
                    <h3 className="section-header">{t('settings.calendar.settingsSectionTitle')}</h3>
                    <form className="address-form">
                        <div className="gs-settings-grid">
                            <SettingsField label={t('settings.calendar.advancedBookingWindow')} htmlFor="advancedBookingWindow">
                                <NumberWithSuffix
                                    id="advancedBookingWindow"
                                    integer
                                    max={12}
                                    value={settingsData?.advancedBookingWindow ?? ''}
                                    onChange={(v) => handleInputChange('advancedBookingWindow', v)}
                                    suffix={t('settings.calendar.advancedBookingWindowSuffix', { count: Number(settingsData?.advancedBookingWindow) || 0 })}
                                />
                                <small className="form-text text-muted">
                                    {t('settings.calendar.advancedBookingWindowHint')}
                                </small>
                            </SettingsField>

                            <ToggleRow
                                label={t('settings.calendar.allowBookingOutsideHours')}
                                description={t('settings.calendar.allowBookingOutsideHoursDescription')}
                                checked={!!settingsData?.allowBookingOutsideBusinessHours}
                                onChange={(v) => handleInputChange('allowBookingOutsideBusinessHours', v)}
                            />
                            <ToggleRow
                                label={t('settings.calendar.allowWaitlist')}
                                description={t('settings.calendar.allowWaitlistDescription')}
                                checked={!!settingsData?.allowWaitlist}
                                onChange={(v) => handleInputChange('allowWaitlist', v)}
                            />
                            {/* Removed (D8): waitlistWindowDays, notifyWaitlistAvailableSpot
                               and waitlistAvailableSpotConfirmationWindow had NO consumer —
                               the offer notification (notifyOffer) always fires and its
                               deadline comes from the per-entry validUntil, not these
                               business settings. The DB columns stay dormant. */}
                            <ToggleRow
                                label={t('settings.calendar.waitingRoom')}
                                description={t('settings.calendar.waitingRoomDescription')}
                                checked={!!settingsData?.enableWaitingRoom}
                                onChange={(v) => handleInputChange('enableWaitingRoom', v)}
                            />

                            <ToggleRow
                                label={t('settings.calendar.acceptOutsideLocation')}
                                description={t('settings.calendar.acceptOutsideLocationDescription')}
                                checked={!!settingsData?.acceptBookingOutsideLocation}
                                onChange={(v) => handleInputChange('acceptBookingOutsideLocation', v)}
                            />
                        </div>
                    </form>
                </div>
            </div>
            <div className="side-content-vertical">
                {/* Cancellation */}
                <div className="business-section-container separated-container">
                    <h3 className="section-header">{t('settings.calendar.cancellationSectionTitle')}</h3>
                    <form className="address-form">
                        <div className="gs-settings-grid">
                            <ToggleRow
                                label={t('settings.calendar.allowOnlineCancellation')}
                                description={t('settings.calendar.allowOnlineCancellationDescription')}
                                checked={!!settingsData?.allowOnlineCancellation}
                                onChange={(v) => handleInputChange('allowOnlineCancellation', v)}
                            />
                            <ToggleRow
                                label={t('settings.calendar.cancellationPolicyRequired')}
                                description={t('settings.calendar.cancellationPolicyRequiredDescription')}
                                checked={!!settingsData?.requireCancellationPolicy}
                                onChange={(v) => handleInputChange('requireCancellationPolicy', v)}
                            />

                            <SettingsField
                                label={t('settings.calendar.cancellationPolicy')}
                                htmlFor="cancellationPolicy"
                                description={t('settings.calendar.cancellationPolicyDescription')}
                            >
                                <input
                                    type="text"
                                    id="cancellationPolicy"
                                    maxLength={1000}
                                    data-cy="biz-cal-cancellation-policy-input"
                                    value={settingsData?.cancellationPolicy || ''}
                                    onChange={(e) =>
                                        handleInputChange('cancellationPolicy', e.target.value)
                                    }
                                    placeholder={t('settings.calendar.cancellationPolicyPlaceholder')}
                                    disabled={!settingsData?.requireCancellationPolicy}
                                />
                            </SettingsField>

                            <SettingsField
                                label={t('settings.calendar.cancellationWindow')}
                                htmlFor="cancellationWindow"
                                description={t('settings.calendar.cancellationWindowDescription')}
                            >
                                <NumberWithSuffix
                                    id="cancellationWindow"
                                    integer
                                    max={720}
                                    value={settingsData?.cancellationWindow ?? ''}
                                    onChange={(v) => handleInputChange('cancellationWindow', v)}
                                    suffix={t('settings.calendar.cancellationWindowSuffix', { count: Number(settingsData?.cancellationWindow) || 0 })}
                                    placeholder={t('settings.calendar.cancellationWindowPlaceholder')}
                                />
                            </SettingsField>

                            <SettingsField
                                label={t('settings.calendar.cancellationFee')}
                                htmlFor="cancellationFee"
                                description={t('settings.calendar.cancellationFeeDescription')}
                            >
                                <NumberWithSuffix
                                    id="cancellationFee"
                                    max={100}
                                    value={settingsData?.cancellationFee ?? ''}
                                    onChange={(v) => handleInputChange('cancellationFee', v)}
                                    suffix={t('settings.calendar.cancellationFeeSuffix')}
                                    placeholder={t('settings.calendar.cancellationFeePlaceholder')}
                                />
                            </SettingsField>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default BusinessCalendarComponent;
